import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import { getArtworks, getArtists, getExhibitions, getJournal, getVideos } from '@/lib/api';
import InlinePlayer from '@/components/InlinePlayer';
import PictureImg from '@/components/PictureImg';
import { getArtworkAspectRatio } from '@/lib/utils';

const fmtDate = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
const fmtShortDate = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long' });

export default function Home() {
    useDocumentMeta({
        title: 'Maison Touré · Fine Contemporary African Art Gallery, Sydney',
        description: 'Maison Touré is a Sydney-based gallery dedicated to contemporary African art. Curated works, private viewings by appointment, provenance-led.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const h = settings?.home || {};
    const [data, setData] = useState({ artworks: [], artists: [], exhibitions: [], journal: [], videos: [] });
    const [heroLqip, setHeroLqip] = useState(null);

    // Same tiny blurred hero preview the static pre-render uses (see
    // public/index.html). Applied here too so the placeholder is already
    // in place -- not a blank/solid gap -- for the moment between React
    // mounting and the full hero photo finishing its download. The browser
    // typically already has this response cached from the static hero's
    // own fetch of the same URL, so this resolves near-instantly.
    useEffect(() => {
        let alive = true;
        fetch('/api/hero/lqip').then((r) => r.json()).then((d) => {
            if (alive && d && d.lqip) setHeroLqip(d.lqip);
        }).catch(() => { /* keep no background -- .hero-fade renders as before */ });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        Promise.all([
            getArtworks({ featured: true }),
            getArtists(),
            getExhibitions(),
            getJournal(),
            getVideos({ featured: true }),
        ]).then(([artworks, artists, exhibitions, journal, videos]) => {
            setData({ artworks, artists, exhibitions, journal, videos });
        });
    }, []);

    // Homepage-only performance hint: preload the hero background image with
    // high fetch priority so it starts downloading before any other route
    // asset request, and mirror the WebP variant that PictureImg emits so
    // WebP-capable browsers preload the exact URL <picture> will ultimately
    // pick. The link is removed on unmount so navigating away doesn't leave
    // orphan preloads in <head>.
    //
    // Deliberately narrow: no animation, layout, typography, styling, or
    // component changes — this is a resource hint only.
    useEffect(() => {
        const hero = h.hero_image;
        if (!hero || typeof document === 'undefined') return;

        const webp = /\.(jpe?g|png)(\?.*)?$/i.test(hero)
            ? hero.replace(/\.(jpe?g|png)(\?.*)?$/i, '.webp$2')
            : null;

        const key = `hero-preload::${hero}`;
        if (document.getElementById(key)) return;

        const link = document.createElement('link');
        link.id = key;
        link.rel = 'preload';
        link.as = 'image';
        link.setAttribute('fetchpriority', 'high');
        if (webp) {
            // Preload the WebP variant (what modern browsers will actually
            // request from <picture><source type="image/webp">). The `type`
            // filter ensures browsers without WebP support skip this hint
            // and fall back to loading the JPEG/PNG normally via <img>.
            link.setAttribute('imagesrcset', webp);
            link.type = 'image/webp';
            link.href = hero; // fallback for user-agents that ignore imagesrcset
        } else {
            link.href = hero;
        }
        document.head.appendChild(link);

        return () => {
            link.parentNode && link.parentNode.removeChild(link);
        };
    }, [h.hero_image]);

    const current = data.exhibitions.find((e) => e.status === 'current');
    const featuredArtists = data.artists.filter((a) => a.featured).slice(0, 3);

    return (
        <div ref={reveal} data-testid="home-page">
            {/* Site-wide Organization / ArtGallery / WebSite structured data
                is declared statically in `public/index.html`, so no per-page
                gallery JSON-LD is injected here (avoids duplicate entities). */}
            {/* ============ HERO ============ */}
            <section className="relative min-h-[100svh] w-full" data-testid="hero-section">
                <div
                    className="absolute inset-0 hero-fade"
                    style={heroLqip ? { backgroundImage: `url(${heroLqip})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                >
                    {/* Fallback src/alt so the hero image renders on first paint instead of
                        waiting on the useSiteSettings() fetch -- headline/subtitle/CTAs below
                        already had this fallback pattern, the image was the one gap. */}
                    <PictureImg
                        src={h.hero_image || '/api/uploads/dedadd341743428b90a48796fc5fbd38.jpg'}
                        alt={h.hero_image_credit || 'Ibou Diagne · Dakar No. 1, 2025'}
                        className="w-full h-full object-cover"
                        style={{ filter: 'brightness(0.94) contrast(1.04)' }}
                        loading="eager"
                        fetchPriority="high"
                        decoding="sync"
                    />
                </div>
                <div className="relative z-10 min-h-[100svh] flex flex-col justify-between max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-40 pb-16 md:pb-20">
                    <div className="reveal" />

                    <div className="max-w-4xl">
                        <h1
                            className="reveal serif font-light"
                            style={{
                                color: 'var(--bg)',
                                fontSize: 'clamp(46px, 7.4vw, 126px)',
                                lineHeight: 0.94,
                                letterSpacing: '-0.025em',
                                textShadow: '0 1px 28px rgba(20,18,15,0.28)',
                            }}
                        >
                            {h.hero_headline_line1 || 'A house for'}<br />
                            <em className="serif-italic" style={{ fontWeight: 400 }}>{h.hero_headline_italic || 'contemporary'}</em><br />
                            {(h.hero_headline_line3 || 'African art.').split(' ').map((w, i, arr) => i === arr.length - 1 ? <span key={i} style={{ whiteSpace: 'nowrap' }}>&nbsp;{w}</span> : <span key={i}>{i > 0 ? ' ' : ''}{w}</span>)}
                        </h1>
                        <p
                            className="reveal mt-12 max-w-lg"
                            style={{
                                color: 'rgba(249,248,246,0.88)',
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 18.5,
                                lineHeight: 1.6,
                                letterSpacing: '-0.005em',
                            }}
                        >
                            {h.hero_subtitle || 'Maison Touré is a Sydney-based gallery dedicated to contemporary African art. Through carefully curated exhibitions and close collaborations with artists, the gallery introduces significant voices to collectors, institutions and designers across Australia and beyond.'}
                        </p>
                        <div className="reveal mt-14 flex flex-wrap items-baseline gap-x-12 gap-y-6">
                            <Link
                                to={h.hero_cta_primary_link || '/artworks'}
                                data-testid="hero-cta-artworks"
                                className="btn-ghost"
                                style={{ color: 'var(--bg)' }}
                            >
                                {h.hero_cta_primary_label || 'The collection'}
                            </Link>
                            <Link
                                to={h.hero_cta_secondary_link || '/private-viewing'}
                                data-testid="hero-cta-enquire"
                                className="btn-ghost"
                                style={{ color: 'var(--bg)' }}
                            >
                                {h.hero_cta_secondary_label || 'Private viewing'}
                            </Link>
                        </div>
                    </div>

                    <div className="reveal flex justify-end items-end" style={{ color: 'rgba(249,248,246,0.78)' }}>
                        <span
                            style={{
                                fontStyle: 'italic',
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 15.5,
                                letterSpacing: '0',
                            }}
                        >
                            {h.hero_image_credit || 'Ibou Diagne · Dakar No. 1, 2025'}
                        </span>
                    </div>
                </div>
            </section>

            {/* ============ SELECTED WORKS ============ */}
            <section
                className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-40 md:pt-56 pb-40 md:pb-56"
                data-testid="featured-artworks"
            >
                <header className="reveal mb-24 md:mb-32 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                    <div>
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>I &nbsp;—&nbsp; Selected Works</span>
                        <h2
                            className="serif mt-6 leading-[1.02]"
                            style={{ fontSize: 'clamp(36px, 4.8vw, 84px)', letterSpacing: '-0.022em' }}
                        >
                            Recent <em className="serif-italic">acquisitions.</em>
                        </h2>
                    </div>
                    <Link to="/artworks" className="btn-ghost self-start md:self-end">
                        The full collection
                    </Link>
                </header>

                <div className="editorial-grid" style={{ gap: '40px' }}>
                    {data.artworks.slice(0, 4).map((w, i) => {
                        const layouts = [
                            'md:col-span-7 md:col-start-1',
                            'md:col-span-4 md:col-start-9 md:mt-40',
                            'md:col-span-5 md:col-start-2 md:mt-24',
                            'md:col-span-6 md:col-start-7',
                        ];
                        return (
                            <Link
                                key={w.slug}
                                to={`/artworks/${w.slug}`}
                                className={`reveal group ${layouts[i] || ''}`}
                                data-testid={`featured-artwork-${w.slug}`}
                                style={{ marginTop: i > 0 ? undefined : 0 }}
                            >
                                <div className="img-wrap img-wrap--natural" style={{ background: 'var(--bg-3)', aspectRatio: getArtworkAspectRatio(w) }} onContextMenu={(e) => e.preventDefault()}>
                                    <PictureImg src={w.image} alt={w.title} loading="lazy" className="no-save" />
                                </div>
                                <div className="mt-8 md:mt-10">
                                    <div
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontWeight: 400,
                                            fontSize: 17,
                                            color: 'var(--ink-2)',
                                            letterSpacing: '0',
                                        }}
                                    >
                                        {w.artist_name}
                                    </div>
                                    <h3
                                        className="mt-2"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontWeight: 500,
                                            fontSize: 'clamp(28px, 3vw, 42px)',
                                            lineHeight: 1.08,
                                            letterSpacing: '-0.012em',
                                        }}
                                    >
                                        {w.title},{' '}
                                        <span style={{ color: 'var(--ink-3)' }}>{w.year}</span>
                                    </h3>
                                    <div className="mt-5 flex items-baseline justify-between gap-6">
                                        <span
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontSize: 15,
                                                color: 'var(--ink-3)',
                                            }}
                                        >
                                            {w.medium.split(',')[0]}
                                        </span>
                                        <span
                                            style={{
                                                fontFamily: "'Jost', sans-serif",
                                                fontSize: 10,
                                                letterSpacing: '0.34em',
                                                textTransform: 'uppercase',
                                                color: 'var(--ink-2)',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {w.price_on_request ? 'On Request' : `${w.currency} ${w.price?.toLocaleString()}`}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ============ STATEMENT ============ */}
            <section className="rule-top" data-testid="home-statement">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-40 md:py-56">
                    <div className="reveal max-w-3xl mx-auto text-center">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{h.statement_overline || 'Maison Touré'}</span>
                        <p
                            className="mt-10"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(28px, 2.6vw, 44px)',
                                lineHeight: 1.32,
                                letterSpacing: '-0.013em',
                                color: 'var(--ink)',
                            }}
                        >
                            {h.statement_text || 'A house for the considered presentation of contemporary African art, where collectors are welcomed with the same care as the artists we represent.'}
                        </p>
                    </div>
                </div>
            </section>

            {/* ============ FEATURED ARTISTS ============ */}
            <section className="rule-top" data-testid="featured-artists">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-40 md:py-56">
                    <header className="reveal mb-24 md:mb-32 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                        <div>
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>II &nbsp;—&nbsp; The Programme</span>
                            <h2
                                className="serif mt-6 leading-[1.02]"
                                style={{ fontSize: 'clamp(36px, 4.8vw, 84px)', letterSpacing: '-0.022em' }}
                            >
                                A small, considered <em className="serif-italic">roster.</em>
                            </h2>
                        </div>
                        <Link to="/artists" className="btn-ghost self-start md:self-end">
                            All artists
                        </Link>
                    </header>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-24">
                        {featuredArtists.map((a, i) => (
                            <Link
                                key={a.slug}
                                to={`/artists/${a.slug}`}
                                className={`reveal group block ${i === 1 ? 'md:mt-24' : ''} ${i === 2 ? 'md:mt-10' : ''}`}
                                data-testid={`featured-artist-${a.slug}`}
                            >
                                <div className="img-wrap aspect-[3/4]" style={{ background: 'var(--bg-3)' }}>
                                    <PictureImg src={a.portrait} alt={a.name} loading="lazy" />
                                </div>
                                <div className="mt-8 md:mt-10">
                                    <h3
                                        className="serif"
                                        style={{
                                            fontSize: 'clamp(28px, 2.8vw, 42px)',
                                            letterSpacing: '-0.015em',
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {a.name}
                                    </h3>
                                    <div
                                        className="mt-2"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontSize: 16,
                                            color: 'var(--ink-2)',
                                        }}
                                    >
                                        {a.origin} &nbsp;·&nbsp; {a.born}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ CURRENT EXHIBITION ============ */}
            {current && (
                <section className="rule-top" data-testid="current-exhibition">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-40 md:py-56 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
                        <div className="md:col-span-7 reveal">
                            <div className="img-wrap aspect-[5/4]" style={{ background: 'var(--bg-3)' }}>
                                <PictureImg src={current.cover_image} alt={current.title} loading="lazy" />
                            </div>
                        </div>
                        <div className="md:col-span-4 md:col-start-9 reveal">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>Currently on view</span>
                            <h2
                                className="mt-6 leading-[1.04]"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontWeight: 500,
                                    fontSize: 'clamp(36px, 4.4vw, 68px)',
                                    letterSpacing: '-0.018em',
                                }}
                            >
                                {current.title}
                            </h2>
                            <div
                                className="mt-3"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontSize: 18,
                                    color: 'var(--ink-2)',
                                }}
                            >
                                {current.subtitle}
                            </div>
                            <p
                                className="mt-10"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontWeight: 400,
                                    fontSize: 17,
                                    lineHeight: 1.7,
                                    color: 'var(--ink-2)',
                                }}
                            >
                                {current.description}
                            </p>
                            <div
                                className="mt-10"
                                style={{
                                    fontFamily: "'Jost', sans-serif",
                                    fontSize: 10.5,
                                    letterSpacing: '0.34em',
                                    textTransform: 'uppercase',
                                    color: 'var(--ink-2)',
                                    lineHeight: 1.8,
                                }}
                            >
                                {fmtShortDate(current.start_date)} — {fmtDate(current.end_date)}<br />
                                {current.location}
                            </div>
                            <div className="mt-12">
                                <Link
                                    to={`/exhibitions/${current.slug}`}
                                    className="btn-ghost"
                                    data-testid="current-exhibition-cta"
                                >
                                    View the exhibition
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ============ ABOUT — FATYM ============ */}
            <section className="rule-top" data-testid="home-about">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-40 md:py-56 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
                    <div className="md:col-span-5 reveal">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>III &nbsp;—&nbsp; {h.founder_overline || 'Maison Touré'}</span>
                        <h2
                            className="mt-6 leading-[1.04]"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(36px, 4.4vw, 68px)',
                                letterSpacing: '-0.018em',
                            }}
                        >
                            {h.founder_heading || 'Founded by'} <em className="serif-italic" style={{ fontWeight: 500 }}>{h.founder_heading_italic || 'Fatym Touré.'}</em>
                        </h2>
                        <p
                            className="mt-10"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 18,
                                lineHeight: 1.7,
                                color: 'var(--ink-2)',
                            }}
                        >
                            {h.founder_body || 'Maison Touré was founded by Fatym Touré as a house for contemporary African art. Guided by care, cultural understanding and lasting relationships, the gallery works closely with artists across Africa to present significant works with the attention they deserve. Every exhibition, conversation and acquisition is shaped by a commitment to authenticity, thoughtful curation and a deeply personal collector experience.'}
                        </p>
                        <blockquote
                            className="mt-10 pl-6"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontStyle: 'italic',
                                fontWeight: 400,
                                fontSize: 'clamp(20px, 1.8vw, 28px)',
                                lineHeight: 1.4,
                                color: 'var(--ink)',
                                borderLeft: '1px solid var(--ink)',
                            }}
                        >
                            {h.founder_quote || 'We move at the pace of the artist. Every encounter is guided by care.'}
                        </blockquote>
                        <div className="mt-12">
                            <Link to="/about" className="btn-ghost" data-testid="home-about-cta">
                                About the gallery
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 reveal">
                        <div className="img-wrap aspect-[4/5]" style={{ background: 'var(--bg-3)' }}>
                            {h.founder_image && <PictureImg src={h.founder_image} alt="Maison Touré, Sydney" loading="lazy" />}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ COLLECTOR SERVICES ============ */}
            <section className="rule-top" data-testid="collector-services">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-40 md:py-56">
                    <header className="reveal text-center mb-24 md:mb-32">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>IV &nbsp;—&nbsp; {h.services_overline || "The Collector's Care"}</span>
                        <h2
                            className="mt-6 leading-[1.04] mx-auto max-w-3xl"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(36px, 4.4vw, 68px)',
                                letterSpacing: '-0.018em',
                            }}
                        >
                            {h.services_heading || 'Trust, in'} <em className="serif-italic" style={{ fontWeight: 500 }}>{h.services_heading_italic || 'every detail.'}</em>
                        </h2>
                    </header>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-16 gap-y-16">
                        {(h.services || []).map((s) => (
                            <div key={s.n} className="reveal rule-top pt-10">
                                <span
                                    className="serif-italic"
                                    style={{ fontSize: 22, color: 'var(--ink-3)' }}
                                >
                                    {s.n}
                                </span>
                                <h3
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontWeight: 500,
                                        fontSize: 'clamp(24px, 2.2vw, 32px)',
                                        lineHeight: 1.18,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {s.title}
                                </h3>
                                <p
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 17,
                                        lineHeight: 1.65,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {s.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ FROM THE FILM ARCHIVE ============ */}
            {/* Editorial interlude — kept quieter than the artwork grid so films
               support the gallery experience rather than compete with it. */}
            {data.videos.length > 0 && (
                <section className="rule-top" data-testid="home-films">
                    <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-20 py-48 md:py-64">
                        <header className="reveal mb-20 md:mb-28">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>V &nbsp;—&nbsp; From the Film Archive</span>
                            <h2
                                className="serif mt-6 leading-[1.02]"
                                style={{ fontSize: 'clamp(32px, 4vw, 68px)', letterSpacing: '-0.02em' }}
                            >
                                Quiet <em className="serif-italic">cinema.</em>
                            </h2>
                        </header>
                        <div className={`grid grid-cols-1 ${data.videos.length > 1 ? 'md:grid-cols-2' : ''} ${data.videos.length > 2 ? 'lg:grid-cols-3' : ''} gap-x-10 gap-y-16 max-w-[1200px] mx-auto`}>
                            {data.videos.slice(0, 3).map((v, i) => (
                                <article
                                    key={v.slug}
                                    className={`reveal group ${i === 1 ? 'md:mt-12' : ''}`}
                                    data-testid={`home-film-${v.slug}`}
                                >
                                    <InlinePlayer video={v} testid={`home-film-${v.slug}`} />
                                    <div className="mt-6">
                                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{v.category}</span>
                                        <h3
                                            className="mt-3"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(20px, 1.7vw, 26px)',
                                                lineHeight: 1.18,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {v.title}
                                        </h3>
                                    </div>
                                </article>
                            ))}
                        </div>
                        <div className="reveal mt-20 md:mt-28 flex justify-center">
                            <Link to="/film" className="btn-ghost" data-testid="home-films-archive-link">
                                Explore the Film Archive
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* ============ JOURNAL ============ */}
            <section className="rule-top" data-testid="home-journal">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-40 md:py-56">
                    <header className="reveal mb-24 md:mb-32 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                        <div>
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>VI &nbsp;—&nbsp; The Journal</span>
                            <h2
                                className="serif mt-6 leading-[1.02]"
                                style={{ fontSize: 'clamp(36px, 4.8vw, 84px)', letterSpacing: '-0.022em' }}
                            >
                                Field <em className="serif-italic">notes.</em>
                            </h2>
                        </div>
                        <Link to="/journal" className="btn-ghost self-start md:self-end">
                            All entries
                        </Link>
                    </header>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-20">
                        {data.journal.slice(0, 3).map((j, i) => (
                            <Link
                                key={j.slug}
                                to={`/journal/${j.slug}`}
                                className={`reveal group block ${i === 1 ? 'md:mt-20' : ''}`}
                                data-testid={`journal-card-${j.slug}`}
                            >
                                <div className="img-wrap aspect-[4/3]" style={{ background: 'var(--bg-3)' }}>
                                    <PictureImg src={j.cover_image} alt={j.title} loading="lazy" />
                                </div>
                                <div className="mt-8">
                                    <span className="overline" style={{ color: 'var(--ink-2)' }}>{j.category}</span>
                                    <h3
                                        className="mt-4"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontWeight: 500,
                                            fontSize: 'clamp(22px, 2vw, 30px)',
                                            lineHeight: 1.2,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {j.title}
                                    </h3>
                                    <p
                                        className="mt-4"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontWeight: 400,
                                            fontSize: 16,
                                            lineHeight: 1.6,
                                            color: 'var(--ink-2)',
                                        }}
                                    >
                                        {j.excerpt}
                                    </p>
                                    <div
                                        className="mt-5"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontSize: 14,
                                            color: 'var(--ink-3)',
                                        }}
                                    >
                                        {fmtDate(j.published_at)}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
